<template>
  <div class="buying-container">
    <HeaderComp />
    <h1 class="page-heading">BUYING</h1>

    <!-- Filters Section -->
    <div class="filters">
      <label for="location-select">Location:</label>
      <select v-model="selectedLocation" id="location-select" @change="fetchInventorySummary">
        <option value="" disabled selected>Select Location</option>
        <option value="F">Fresno</option>
        <option value="D">Denver</option>
        <option value="K">Knoxville</option>
        <option value="L">Lakeland</option>
      </select>

      <label for="material-select">Material:</label>
      <select v-model="selectedMaterial" id="material-select" @change="fetchInventorySummary">
        <option disabled value="" selected>Select Material</option>
        <option v-for="material in materials" :key="material" :value="material">
          {{ material }}
        </option>
      </select>
    </div>

    <!-- Table Section -->
    <div class="table-container">
      <table>
        <thead>
          <tr>
            <th @click="sortBy('item_no')">Item No <span>{{ getSortIcon('item_no') }}</span></th>
            <th @click="sortBy('qty_on_hand')">QOH <span>{{ getSortIcon('qty_on_hand') }}</span></th>
            <th @click="sortBy('daily_usage')">Daily Usage <span>{{ getSortIcon('daily_usage') }}</span></th>
            <th @click="sortBy('allocated_on_production')">Allocated on Production <span>{{ getSortIcon('allocated_on_production') }}</span></th>
            <th @click="sortBy('allocated_on_sales')">Allocated on Sales <span>{{ getSortIcon('allocated_on_sales') }}</span></th>
            <th @click="sortBy('net_qoh')">Net QOH <span>{{ getSortIcon('net_qoh') }}</span></th>
            <th @click="sortBy('net_days_on_hand')">Net Days On Hand <span>{{ getSortIcon('net_days_on_hand') }}</span></th>
            <th>Quantity to Purchase</th>
            <th>Select</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in sortedAndFilteredItems" :key="item.item_no">
            <td>{{ item.item_no }}</td>
            <td>{{ Math.round(item.qty_on_hand) }}</td>
            <td>{{ item.daily_usage }}</td>
            <td>{{ item.allocated_on_production }}</td>
            <td>{{ item.allocated_on_sales }}</td>
            <td>{{ Math.round(item.net_qoh) }}</td>
            <td>{{ calculateNetDaysOnHand(item.net_qoh, item.daily_usage) }}</td>
            <td>{{ calculateQuantityToPurchase(item.net_qoh, item.daily_usage) }}</td>
            <td><input type="checkbox" v-model="selectedItems" :value="item" /></td>
          </tr>
        </tbody>
      </table>
    </div>
    <p v-if="items.length === 0">Please select a filter to view data.</p>

    <!-- Create Purchase Order Button -->
    <button @click="showPurchaseOrderPopup" :disabled="selectedItems.length === 0">
      Create Purchase Order
    </button>

    <!-- Purchase Order Popup -->
<div v-if="showPopup" class="popup-overlay">
  <div class="popup-content">
    <h2>Purchase Order Summary</h2>
    <table>
      <thead>
        <tr>
          <th>Item No</th>
          <th>Quantity</th>
          <th>Standard Cost</th>
          <th>Extended Cost</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in selectedItems" :key="item.item_no">
          <td>{{ item.item_no }}</td>
          <td>{{ formatNumber(calculateQuantityToPurchase(item.net_qoh, item.daily_usage)) }}</td>
          <td>{{ formatNumber(item.std_cost) }}</td>
          <td>{{ formatNumber(calculateExtendedCost(item)) }}</td>
        </tr>
      </tbody>
    </table>
    <div class="total">
      <strong>Total:</strong> {{ formatNumber(calculateTotalCost()) }}
    </div>

    <label for="vendor-select">Vendor:</label>
    <select id="vendor-select" v-model="selectedVendor">
      <option value="" disabled selected>Select Vendor</option>
      <option v-for="vendor in vendors" :key="vendor" :value="vendor">{{ vendor }}</option>
    </select>

    <label for="delivery-date">Requested Delivery Date:</label>
    <input type="date" id="delivery-date" v-model="deliveryDate" />

    <button @click="submitPurchaseOrder">Submit Purchase Order</button>
    <button @click="closePurchaseOrderPopup">Cancel</button>
  </div>
</div>

  </div>
</template>

<script>
import axios from 'axios';
import HeaderComp from './HeaderComp.vue';

export default {
  components: { HeaderComp },
  data() {
    return {
      selectedLocation: '',
      selectedMaterial: '',
      items: [],
      materials: [],
      vendors: ['Vendor A', 'Vendor B', 'Vendor C'],
      selectedVendor: '',
      deliveryDate: '',
      selectedItems: [],
      showPopup: false,
      sortKey: 'item_no',
      sortDirection: 'asc',
    };
  },
  computed: {
    sortedAndFilteredItems() {
      return [...this.items].sort((a, b) => {
        const comparison = a[this.sortKey] > b[this.sortKey] ? 1 : a[this.sortKey] < b[this.sortKey] ? -1 : 0;
        return this.sortDirection === 'asc' ? comparison : -comparison;
      });
    },
  },
  methods: {
    fetchMaterials() {
      axios.get('/getMaterialTypes').then((response) => {
        this.materials = response.data;
      });
    },
    fetchInventorySummary() {
    if (this.selectedLocation && this.selectedMaterial) {
        axios
            .get('/getInventorySummary', {
                params: {
                    loc: this.selectedLocation,
                    type: this.selectedMaterial,
                },
            })
            .then((response) => {
                this.items = response.data.map((item) => ({
                    ...item,
                    std_cost: item.std_cost || 0, // Ensure std_cost is initialized
                }));
            });
    } else {
        this.items = [];
    }
},
    calculateNetDaysOnHand(netQoh, dailyUsage) {
      if (dailyUsage > 0) {
        return (netQoh / dailyUsage).toFixed(1);
      }
      return '-';
    },
    calculateQuantityToPurchase(netQoh, dailyUsage) {
      const daysOnHand = this.calculateNetDaysOnHand(netQoh, dailyUsage);
      if (daysOnHand < 0) {
        return Math.round((Math.abs(daysOnHand) + 10) * dailyUsage);
      } else if (daysOnHand < 10) {
        return Math.round((10 - daysOnHand) * dailyUsage);
      }
      return 0;
    },
    calculateExtendedCost(item) {
      return item.std_cost ? (item.std_cost * this.calculateQuantityToPurchase(item.net_qoh, item.daily_usage)).toFixed(2) : 0;
    },
    calculateTotalCost() {
      return this.selectedItems
        .reduce((total, item) => total + parseFloat(this.calculateExtendedCost(item) || 0), 0)
        .toFixed(2);
    },
    showPurchaseOrderPopup() {
      this.showPopup = true;
    },
    closePurchaseOrderPopup() {
      this.showPopup = false;
    },
    submitPurchaseOrder() {
      console.log('Submitting purchase order for:', this.selectedItems);
      console.log('Vendor:', this.selectedVendor);
      console.log('Delivery Date:', this.deliveryDate);
      this.closePurchaseOrderPopup();
    },
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortDirection = 'asc';
      }
    },
    getSortIcon(key) {
      if (this.sortKey === key) {
        return this.sortDirection === 'asc' ? '▲' : '▼';
      }
      return '';
    },

    formatNumber(value) {
  if (value === null || value === undefined) return '-'; // Handle null or undefined
  return new Intl.NumberFormat('en-US').format(value);
},

  },
  mounted() {
    this.fetchMaterials();
  },
};
</script>

<style scoped>
.buying-container {
  font-family: Garamond, serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #cbbfbf;
  background-color: #0b0101;
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.filters {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
  width: 80%;
}

.filters label {
  margin-right: 10px;
  color: #cbbfbf;
}

.filters select {
  padding: 5px;
  font-family: Garamond, serif;
  background-color: #2c3e50;
  color: #cbbfbf;
  border: 1px solid #cbbfbf;
  border-radius: 4px;
}

.table-container {
  max-height: 650px;
  overflow-y: auto;
  border: 1px solid #cbbfbf;
  width: 80%;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th, td {
  border: 1px solid #cbbfbf;
  padding: 8px;
  text-align: left;
}

th {
  background-color: #2c3e50;
  color: #cbbfbf;
  position: sticky;
  top: 0;
}

td {
  background-color: #0b0101;
  color: #cbbfbf;
}

tr:nth-child(even) td {
  background-color: #2c3e50;
}

tr:hover td {
  background-color: #403d3d;
}

.page-heading {
  font-family: 'Cormorant Garamond', serif;
  font-size: 36px;
  color: #cbbfbf;
  margin-bottom: 20px;
}

.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.popup-content {
  background: #2c3e50;
  color: #cbbfbf;
  padding: 20px;
  border-radius: 8px;
  text-align: left;
}

button {
  margin-top: 20px;
  padding: 10px 20px;
  font-family: Garamond, serif;
  background-color: #2c3e50;
  color: #cbbfbf;
  border: 1px solid #cbbfbf;
  border-radius: 4px;
  cursor: pointer;
}

button:disabled {
  background-color: #403d3d;
  cursor: not-allowed;
}

.total {
  margin-top: 20px;
}
</style>
